import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, DollarSign } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { formatNumber } from "@/utils/format";
import {
  extractApiError,
  getValorCarradaVigente,
  registrarReajusteValorCarrada,
  type ContratoDto,
} from "@/services/locacoes";

interface Props {
  open: boolean;
  contrato: ContratoDto | null;
  busy?: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export function ModalReajusteValorCarrada({ open, contrato, busy, onClose, onSaved }: Props) {
  const [valorUnitario, setValorUnitario] = useState("");
  const [observacao, setObservacao] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [avisoSync, setAvisoSync] = useState(false);

  const valorVigente = useMemo(() => {
    if (!contrato) return null;
    return getValorCarradaVigente(contrato.valorCarradaVigencias);
  }, [contrato]);

  useEffect(() => {
    if (!open || !contrato) return;
    setObservacao("");
    setError(null);
    setAvisoSync(false);
    const vigente = getValorCarradaVigente(contrato.valorCarradaVigencias);
    setValorUnitario(vigente != null ? String(vigente) : "");
  }, [open, contrato]);

  const handleSubmit = async () => {
    if (!contrato) return;
    setError(null);
    const valor = Number(valorUnitario);
    if (!Number.isFinite(valor) || valor <= 0) {
      setError("Informe o novo valor unitário.");
      return;
    }
    if (valorVigente != null && Math.abs(valor - valorVigente) < 1e-9) {
      setError("O novo valor é igual ao vigente.");
      return;
    }

    setSaving(true);
    try {
      await registrarReajusteValorCarrada(contrato.id, {
        valorUnitario: valor,
        observacao: observacao.trim(),
      });
      await onSaved();
      setAvisoSync(true);
    } catch (e) {
      setError(extractApiError(e, "Erro ao registrar reajuste."));
    } finally {
      setSaving(false);
    }
  };

  const handleAckAviso = () => {
    setAvisoSync(false);
    onClose();
  };

  if (avisoSync) {
    return (
      <Modal
        open={open}
        onClose={handleAckAviso}
        title={
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            </div>
            <span className="font-bold text-slate-900">Conferência necessária</span>
          </div>
        }
        size="md"
        footer={
          <div className="flex justify-end">
            <Button type="button" onClick={handleAckAviso}>
              Entendi — vou alinhar com os apontadores
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm text-slate-700">
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-950">
            O valor unitário do contrato foi alterado. O engenheiro responsável pela obra deve
            <strong> conferir as carradas com os apontadores</strong> e solicitar a{" "}
            <strong>sincronização do aplicativo</strong> agora.
          </p>
          <ul className="list-disc space-y-1 pl-5 text-slate-600">
            <li>Carradas já lançadas mantêm o valor da faixa em que foram cadastradas.</li>
            <li>Novas carradas (após a sincronização) passam a usar o valor vigente atual.</li>
            <li>Sem a sincronização no campo, o apontador pode continuar com catálogo/valores desatualizados.</li>
          </ul>
        </div>
      </Modal>
    );
  }

  const title = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50">
        <DollarSign className="h-4 w-4 text-emerald-600" />
      </div>
      <div>
        <span className="font-bold text-slate-900">Reajuste de valor unitário</span>
        {contrato && (
          <p className="text-xs font-normal text-slate-500">
            {contrato.codigoContrato} · {contrato.clienteSiengeNome}
          </p>
        )}
      </div>
    </div>
  );

  const disabled = busy || saving || !contrato;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={disabled}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => void handleSubmit()} disabled={disabled}>
            {saving ? "Salvando…" : "Registrar reajuste"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
        )}
        <p className="text-sm text-slate-600">
          Cada alteração vale a partir do horário do cadastro. É possível ter mais de um valor no mesmo
          dia; o início da alteração não é movido para o dia anterior.
        </p>
        <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 text-sm text-slate-700">
          Valor unitário vigente agora:{" "}
          <span className="font-semibold">
            {valorVigente != null ? `R$ ${formatNumber(valorVigente, 2)}` : "—"}
          </span>
        </div>
        <Input
          label="Novo valor unitário *"
          type="number"
          min={0}
          step="0.01"
          value={valorUnitario}
          onChange={(e) => setValorUnitario(e.target.value)}
          placeholder="Ex.: 150,00"
        />
        <Input
          label="Observação"
          uppercase
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Ex.: mudança da distância da jazida"
        />
      </div>
    </Modal>
  );
}
